import type { Answer } from '@/speech/understand/understanding';
import { decide, YES_NO, type Menu, type MenuOption, type Rank } from './decide';

/** What the game does after hearing the child: act on an option, and/or say a line. */
export interface Reply {
  pick?: string;
  say?: string;
}

/** The lines a menu conversation speaks, so each page (and age band) can word them. */
export interface MenuLines {
  /** "Did you mean the curly hair?" */
  confirm: (option: MenuOption) => string;
  /** "Say curly, spiky or braids, or tap one." */
  again: (options: readonly MenuOption[]) => string;
}

/** Recorded once each: the check-back per option label, and one fixed "again" line. */
export const DEFAULT_LINES: MenuLines = {
  confirm: (o) => confirmLine(o.label),
  again: () => AGAIN_LINE,
};

export const AGAIN_LINE = '[warm] Hmm, I didn’t catch that. Say the one you want, or tap it.';

/** After a "no" to a check-back where the options stay on screen (the Tongue Lab). */
export const RETRY_LINE = '[warm] Okay. Say it again, or tap your answer.';

export function confirmLine(label: string): string {
  return `[curious] Did you mean ${label}?`;
}

/** "a, b or c". */
export function spokenList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1] ?? ''}`;
}

/**
 * One voice menu's conversation (docs/design/voice-control.md): each thing the child says goes
 * through `decide`; a lean of the classifier becomes "did you mean ...?" and the next utterance
 * answers that; a question gets its approved answer and the menu stays open.
 */
export class MenuConversation {
  private pending: MenuOption | null = null;

  private menu: Menu;
  private readonly rank: Rank | undefined;
  private readonly answer: (text: string) => Promise<Answer>;
  private readonly lines: MenuLines;

  constructor(
    menu: Menu,
    rank: Rank | undefined,
    answer: (text: string) => Promise<Answer>,
    lines: MenuLines = DEFAULT_LINES,
  ) {
    this.menu = menu;
    this.rank = rank;
    this.answer = answer;
    this.lines = lines;
  }

  /** A new question (the page moved on): any check-back is dropped. */
  setMenu(menu: Menu): void {
    this.menu = menu;
    this.pending = null;
  }

  get confirming(): MenuOption | null {
    return this.pending;
  }

  async hear(text: string): Promise<Reply> {
    if (this.pending) {
      const asked = this.pending;
      const yesNo = await decide(text, YES_NO);
      if (yesNo.kind === 'pick') {
        this.pending = null;
        return yesNo.id === 'yes'
          ? { pick: asked.id }
          : { say: this.lines.again(this.menu.options) };
      }
      // Not a yes or no: perhaps the child just named the option again.
    }
    const d = await decide(text, this.menu, this.rank);
    switch (d.kind) {
      case 'pick':
        this.pending = null;
        return { pick: d.id };
      case 'confirm': {
        const option = this.menu.options.find((o) => o.id === d.id);
        if (!option) return { say: this.lines.again(this.menu.options) };
        this.pending = option;
        return { say: this.lines.confirm(option) };
      }
      case 'question': {
        const a = await this.answer(text);
        return { say: a.text };
      }
      case 'unclear':
        if (this.pending) return { say: this.lines.confirm(this.pending) };
        return { say: this.lines.again(this.menu.options) };
    }
  }
}
